import React, { useCallback, useLayoutEffect } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';

import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { NotFoundView, useHeaderButtonOptions } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import {
  GiftCardConfirmationLoadingPlaceholder,
  GiftCardConfirmationModal,
  GiftCardDetailsScreenContent,
} from './components';
import { useGiftCardConfirmationData, useGiftCardData } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardConfirmationScreen = (props: Props) => {
  const { isModal = false } = props;

  const { orderId } = useLocalSearchParams<{ orderId: string }>();
  const navigation = useNavigation();
  const { track } = useTelemetry();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─── Header Controls ─────────────────────────────────────────────────

  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft: renderNoHeaderLeft });

  // ─── Remote Data ─────────────────────────────────────────────────────

  const giftCardConfirmationData = useGiftCardConfirmationData({
    pause: !isLoggedIn,
    orderId,
  });
  const giftCard = giftCardConfirmationData.data?.userGiftCards?.[0];
  const giftCardData = useGiftCardData({
    assetId: giftCard?.assetId ?? '',
    pause: !giftCard?.assetId,
  });

  const giftCardImgUrl = giftCardData.data?.cover.fields.file.url;
  const giftCardImgA11yLabel = giftCardData.data?.title;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackScreenLoadingError = useCallback(() => {
    if (!isLoggedIn) {
      track('gift.confirmation_failure', { error: 'User is not logged in' });
    }

    if (!giftCardConfirmationData.data) {
      track('gift.confirmation_failure', {
        error: 'Unable to fetch required data',
      });
    }
  }, [giftCardConfirmationData.data, isLoggedIn, track]);

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderLoadingView =
    giftCardConfirmationData.isFetching || giftCardData.fetching || isLoggingIn;

  // ─── Effects ─────────────────────────────────────────────────────────

  // The modal has no header, so only the page hides its back button.
  useLayoutEffect(() => {
    if (isModal) {
      return;
    }

    navigation.setOptions({ ...headerLeftOptions, headerBackVisible: false });
  }, [headerLeftOptions, isModal, navigation]);

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderLoadingView) {
    return <GiftCardConfirmationLoadingPlaceholder isModal={isModal} />;
  }

  if (!isLoggedIn || !giftCard) {
    return <NotFoundView onMount={trackScreenLoadingError} withoutFooter={isModal || undefined} />;
  }

  // ─── Derived Data ────────────────────────────────────────────────────

  const { redemptionToken, recipientName, recipientEmail, balance, message } = giftCard;
  const date = giftCardConfirmationData?.data?.deliveryDetail?.deliveryDateTime;

  const redemptionURL = getGiftRedemptionUrl(redemptionToken!);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GiftCardDetailsScreenContent
      orderId={orderId}
      recipientName={recipientName}
      recipientEmail={recipientEmail}
      balance={balance}
      date={date}
      message={message}
      assetUrl={giftCardImgUrl}
      assetA11yLabel={giftCardImgA11yLabel}
      redemptionURL={redemptionURL}
      isModal={isModal}
    />
  );
};

export const GiftCardConfirmationModalScreen = () => {
  return (
    <GiftCardConfirmationModal>
      <GiftCardConfirmationScreen isModal />
    </GiftCardConfirmationModal>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A small helper that, given the relevant hash, returns the gift card
 * redemption URL.
 *
 * Keeps the legacy `/gift` path so shared links still open in app versions
 * that predate the Account gift routes. Newer versions redirect it.
 */
function getGiftRedemptionUrl(redemptionHash: string) {
  const appUrl = getEnvVars().APP_URL as string;

  return new URL(`/gift/redeem/${redemptionHash}`, appUrl).toString();
}

function renderNoHeaderLeft() {
  return null;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isModal?: boolean;
};
